/**
 * Serialize a screenplay back to `document.xml`. Only `<paragraphs>` is rebuilt
 * from the model; the rest of the retained DOM is written back verbatim. This
 * is the load→save fidelity guarantee: anything we don't model is preserved.
 */
import type { Para, ScreenplayDoc, TextRun } from "./types";

const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8"?>';

export interface SerializeOptions {
  /** If set, overwrite info@pagecount; otherwise leave the existing value. */
  pagecount?: number;
}

/** Build a `<text>` element for one run, writing modeled + preserved attrs. */
function buildTextEl(doc: Document, run: TextRun): Element {
  const el = doc.createElement("text");
  if (run.bold) el.setAttribute("bold", "1");
  if (run.italic) el.setAttribute("italic", "1");
  if (run.underline) el.setAttribute("underline", "1");
  if (run.font !== undefined) el.setAttribute("font", run.font);
  if (run.extra) {
    for (const [k, v] of Object.entries(run.extra)) el.setAttribute(k, v);
  }
  // textContent assignment escapes & < > automatically on serialize.
  el.textContent = run.text;
  return el;
}

/** Build a `<para>` element (with its inner `<style>` + `<text>` runs). */
function buildParaEl(doc: Document, para: Para): Element {
  const paraEl = doc.createElement("para");
  if (para.bookmark !== undefined) paraEl.setAttribute("bookmark", para.bookmark);
  if (para.extra) {
    for (const [k, v] of Object.entries(para.extra)) paraEl.setAttribute(k, v);
  }

  const styleEl = doc.createElement("style");
  styleEl.setAttribute("basestyle", para.style);
  if (para.align) styleEl.setAttribute("align", para.align);
  if (para.styleExtra) {
    for (const [k, v] of Object.entries(para.styleExtra)) {
      styleEl.setAttribute(k, v);
    }
  }
  paraEl.appendChild(styleEl);

  for (const run of para.runs) paraEl.appendChild(buildTextEl(doc, run));
  return paraEl;
}

/**
 * Replace the children of `<paragraphs>` with freshly built paras, using
 * Fade In-style 2-space indentation (cosmetic; parser-irrelevant).
 */
function rebuildParagraphs(doc: Document, paragraphsEl: Element, paras: Para[]): void {
  while (paragraphsEl.firstChild) {
    paragraphsEl.removeChild(paragraphsEl.firstChild);
  }
  const indent = (n: number) => doc.createTextNode("\n" + "  ".repeat(n));
  // <paragraphs> sits at depth 1; <para> at depth 2; <style>/<text> at depth 3.
  for (const para of paras) {
    paragraphsEl.appendChild(indent(2));
    const paraEl = buildParaEl(doc, para);
    // Indent the para's own children for readability.
    const children = Array.from(paraEl.childNodes);
    while (paraEl.firstChild) paraEl.removeChild(paraEl.firstChild);
    for (const child of children) {
      paraEl.appendChild(indent(3));
      paraEl.appendChild(child);
    }
    paraEl.appendChild(indent(2));
    paragraphsEl.appendChild(paraEl);
  }
  paragraphsEl.appendChild(indent(1));
}

export function serializeDocument(
  dom: Document,
  model: ScreenplayDoc,
  opts: SerializeOptions = {},
): string {
  const root = dom.documentElement;

  if (opts.pagecount !== undefined) {
    const info = root.getElementsByTagName("info")[0];
    if (info) info.setAttribute("pagecount", String(opts.pagecount));
  }

  const paragraphsEl = root.getElementsByTagName("paragraphs")[0];
  if (!paragraphsEl) {
    throw new Error("serializeDocument: document has no <paragraphs> element");
  }
  rebuildParagraphs(dom, paragraphsEl, model.paras);

  let xml = new XMLSerializer().serializeToString(root);
  // XMLSerializer never emits the declaration; prepend it. Normalize endings.
  xml = xml.replace(/\r\n?/g, "\n");
  return `${XML_DECLARATION}\n${xml}`;
}
